import type { ScrollMeasurement } from "./scrollLayout";

/** Threshold keys consumed scene-wide. Kept identical to the legacy
 *  SceneThresholds shape so consumers don't change. */
export type SceneThresholds = {
  homeServices: number;
  servicesDecks: number;
  decksDwell: number;
  decksEnterEnd: number;
  decksReviews: number;
  decksReverseExit: number;
  gateHide: number;
  heroFadeEnd: number;
};

export type DerivedHomeThresholds = { thresholds: SceneThresholds; tMax: number };

/** Choreography config — the ONLY hand-tuned scroll numbers left on the home
 *  page. Everything is a fraction of a measured segment (or a screens lead),
 *  baked from the previously tuned absolute t values (see
 *  docs/superpowers/plans/2026-06-11-content-driven-scroll.md, conversion
 *  table). Tune feel here; never re-introduce absolute t literals. */
export const HOME_CHOREO = {
  /** Fractions of the hero segment [0, anchors.services]. */
  heroFadeEnd: 0.14,
  gateHide: 0.33,
  homeServices: 0.48,
  /** Fractions of the decks segment [anchors.decks, anchors.reviews].
   *  servicesDecks/decksDwell lowered 0.3/0.53 → 0.08/0.31 (2026-06-11): the
   *  services→decks transition used to start ~1 screen after the last service
   *  had scrolled away, leaving a long dead stretch of empty sky. Now the
   *  glide starts almost immediately; the flight length (0.23 of the segment)
   *  is unchanged. */
  servicesDecks: 0.08,
  decksDwell: 0.31,
  decksEnterEnd: 0.34,
  decksReverseExit: 0.42,
  /** Screens after #reviews starts entering from the viewport bottom. */
  decksReviewsLead: 0.08,
  /** Screens before #reviews reaches the viewport top (exit-fade end / camera
   *  park point). */
  tMaxLead: 0.44,
} as const;

/** Minimal spacing (screens) enforced between consecutive thresholds so a
 *  degenerate layout can never produce out-of-order choreography. */
const MIN_GAP = 0.01;

/** Synthetic measurement mirroring today's desktop content — used as the
 *  pre-measurement fallback so the first frames behave like the old constants. */
export const DEFAULT_HOME_MEASUREMENT: ScrollMeasurement = {
  vh: 1000,
  contentHeight: 15500,
  anchors: { services: 1000, decks: 8000, reviews: 11200 },
};

export function deriveHomeThresholds(
  m: ScrollMeasurement,
): DerivedHomeThresholds | null {
  const { vh } = m;
  if (vh <= 0) return null;
  const services = m.anchors.services / vh;
  const decks = m.anchors.decks / vh;
  const reviews = m.anchors.reviews / vh;
  if (![services, decks, reviews].every(Number.isFinite)) return null;

  const decksLen = Math.max(0, reviews - decks);
  const after = (v: number, prev: number) => Math.max(v, prev + MIN_GAP);

  const homeServices = HOME_CHOREO.homeServices * services;
  const servicesDecks = after(decks + HOME_CHOREO.servicesDecks * decksLen, homeServices);
  const decksDwell = after(decks + HOME_CHOREO.decksDwell * decksLen, servicesDecks);
  const decksEnterEnd = after(decks + HOME_CHOREO.decksEnterEnd * decksLen, decksDwell);
  const decksReverseExit = after(
    decks + HOME_CHOREO.decksReverseExit * decksLen,
    decksEnterEnd,
  );
  const decksReviews = after(
    reviews - 1 + HOME_CHOREO.decksReviewsLead,
    decksReverseExit,
  );
  const tMax = after(reviews - HOME_CHOREO.tMaxLead, decksReviews);

  return {
    thresholds: {
      heroFadeEnd: HOME_CHOREO.heroFadeEnd * services,
      gateHide: HOME_CHOREO.gateHide * services,
      homeServices,
      servicesDecks,
      decksDwell,
      decksEnterEnd,
      decksReverseExit,
      decksReviews,
    },
    tMax,
  };
}
